import { useNavigate, useSearch } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { PendingApproval } from '@/api/approvals-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Button } from '@/components/ui/button'
import { ApprovalDetail } from '@/features/approvals/approval-detail'
import {
  ApprovalAmount,
  BranchCell,
  ExpiryBadge,
  RequestTypeLabel,
} from '@/features/approvals/approval-presentation'
import { branchDisplayName } from '@/features/approvals/branch-display'
import { CheckerCapabilities } from '@/features/approvals/checker-capabilities'
import { describeExpiry, describeWait } from '@/features/approvals/expiry'
import {
  InboxFilters,
  type BranchOption,
  type DeadlineFilter,
} from '@/features/approvals/inbox-filters'
import { MyRequestsPanel } from '@/features/approvals/my-requests'
import { useApprovalInbox, useCheckerReach, useMyRequests } from '@/features/approvals/queries'
import { RequestLookup } from '@/features/approvals/request-lookup'
import { describeRequestType } from '@/features/approvals/request-types'
import { formatDateTime } from '@/lib/datetime'

import type { ApprovalsTab } from './search'

/**
 * The dual-control queue.
 *
 * One screen for every module, because the control is one control: hold the intent, have a second
 * authorised person look at it, then carry it out. A disbursement, a manual journal and a role
 * assignment differ in what they do and not at all in what a checker is being asked for.
 *
 * Two decisions about this screen are worth stating.
 *
 * **It does not refuse a phone.** Everything else that moves money in this application does — see
 * docs/mobile-policy.md — and this is the deliberate exception named there: dual control means an
 * absent approver stops other people working, and a manager countersigning a disbursement from the
 * road is the scenario the control creates rather than an abuse of it. Nothing here is keyed;
 * every decision is a choice plus a sentence, which is the input touch is good at.
 *
 * **Filtering is client-side, and says so.** The endpoint takes no parameters and returns no page.
 * The filtering that matters — what you may approve, where, excluding your own — has already
 * happened on the server, and the controls here only narrow an array the browser is holding.
 */

const PANEL_ID = 'approvals-panel'

/**
 * Nothing is sortable, and the order is not arbitrary: the server returns the queue oldest first,
 * which is the order it also applies its own cut-off in. Re-sorting the page in the browser would
 * present a different order to the one the truncation follows.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const DEFAULT_PAGE_SIZE = 25

const helper = dataTableColumns<PendingApproval>()

export function ApprovalsPage() {
  const query = useApprovalInbox()
  const mine = useMyRequests()
  const reach = useCheckerReach()

  const search = useSearch({ from: '/authenticated/approvals' })
  const navigate = useNavigate()

  // An absent parameter is the queue rather than a blank screen, which is also what a plain
  // /approvals link — every nav entry, every place other than the dual-control notice — should
  // still land on.
  const tab = search.tab ?? 'queue'
  const setTab = (next: ApprovalsTab) => {
    void navigate({
      to: '/approvals',
      search: { tab: next },
      // Switching tabs is not a place someone means to come back to with the Back button.
      replace: true,
    })
  }

  const [selected, setSelected] = useState<PendingApproval | null>(null)

  const [searchText, setSearchText] = useState('')
  const [module, setModule] = useState<string | undefined>(undefined)
  const [branchId, setBranchId] = useState<string | undefined>(undefined)
  const [deadline, setDeadline] = useState<DeadlineFilter>('any')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const rows = useMemo(() => query.data ?? [], [query.data])

  const moduleOptions = useMemo(
    () => [...new Set(rows.map((row) => describeRequestType(row.requestType).module))].sort(),
    [rows],
  )

  /**
   * Branch options come from the queue itself as well as from the branches granted to this user.
   *
   * For a checker with cross-branch reach the server applies no branch predicate to the inbox, so
   * the queue routinely contains branches that appear in no list the client holds. A filter built
   * only from the granted list would offer no way to narrow to them, and would name them nowhere.
   */
  const branchOptions = useMemo<BranchOption[]>(() => {
    const ids = new Set(rows.map((row) => row.branchId))
    for (const branch of reach.branches) {
      ids.add(branch.id)
    }

    return [...ids]
      .map((id) => ({ id, label: branchDisplayName(id, reach.branches) }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [rows, reach.branches])

  const filtered = useMemo(() => {
    const needle = searchText.trim().toLowerCase()

    return rows.filter((row) => {
      if (module !== undefined && describeRequestType(row.requestType).module !== module) {
        return false
      }

      if (branchId !== undefined && row.branchId !== branchId) {
        return false
      }

      if (deadline !== 'any') {
        const expiry = describeExpiry(row.expiresAtUtc)
        const limit = deadline === 'hour' ? 60 : 6 * 60
        // An unreadable deadline is never quietly dropped from a "lapses soon" view: it is
        // exactly the row somebody has to look at.
        if (expiry.urgency !== 'unknown' && expiry.minutesRemaining > limit) {
          return false
        }
      }

      if (needle === '') {
        return true
      }

      const descriptor = describeRequestType(row.requestType)
      return [
        row.description,
        row.makerUsername,
        row.requestId,
        descriptor.code,
        descriptor.label,
      ].some((value) => value.toLowerCase().includes(needle))
    })
  }, [rows, module, branchId, deadline, searchText])

  const pageRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return filtered.slice(start, start + pagination.pageSize)
  }, [filtered, pagination])

  const columns = useMemo<DataTableColumn<PendingApproval>[]>(
    () =>
      helper.columns([
        helper.accessor('description', {
          header: 'Request',
          enableSorting: false,
          cell: (context) => (
            <span className="block max-w-md truncate text-sm font-medium text-heading">
              {context.row.original.description}
            </span>
          ),
          meta: { card: 'title' },
        }),
        helper.accessor('requestType', {
          header: 'Operation',
          enableSorting: false,
          cell: (context) => <RequestTypeLabel requestType={context.row.original.requestType} />,
          meta: { widthClass: 'w-56', card: 'secondary' },
        }),
        helper.accessor('makerUsername', {
          header: 'Submitted by',
          enableSorting: false,
          cell: (context) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm text-body">{context.row.original.makerUsername}</span>
              <span className="truncate text-2xs text-muted">
                {describeWait(context.row.original.madeAtUtc)}
              </span>
            </span>
          ),
          meta: { widthClass: 'w-44', card: 'secondary' },
        }),
        helper.accessor('branchId', {
          header: 'Branch',
          enableSorting: false,
          cell: (context) => (
            <BranchCell branchId={context.row.original.branchId} branches={reach.branches} />
          ),
          meta: { noWrap: true, card: 'secondary' },
        }),
        helper.accessor('amountMinor', {
          header: 'Amount',
          enableSorting: false,
          cell: (context) => <ApprovalAmount approval={context.row.original} />,
          meta: { align: 'right', widthClass: 'w-40', card: 'secondary', cardLabel: 'Amount' },
        }),
        helper.accessor('expiresAtUtc', {
          header: 'Deadline',
          enableSorting: false,
          cell: (context) => <ExpiryBadge expiresAtUtc={context.row.original.expiresAtUtc} />,
          meta: { widthClass: 'w-32', card: 'lead' },
        }),
      ]),
    [reach.branches],
  )

  const hasActiveFilters =
    searchText.trim() !== '' || module !== undefined || branchId !== undefined || deadline !== 'any'

  const clearFilters = () => {
    setSearchText('')
    setModule(undefined)
    setBranchId(undefined)
    setDeadline('any')
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  /** Any change that shortens the list returns to the first page; the old index would be past it. */
  const narrow = (apply: () => void) => {
    apply()
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  return (
    <>
      <PageHeader
        title="Approvals"
        description="The dual-control queue: requests waiting on a second authoriser. A checker sees what the maker submitted, can never be the maker, and the approval re-validates against current balances before anything executes."
        badges={
          query.data && query.data.length > 0 ? (
            <span className="numeric rounded-full bg-brand-fill-subtle px-2 py-0.5 text-2xs font-medium text-brand-text tabular-nums">
              {String(query.data.length)} waiting
            </span>
          ) : null
        }
        actions={
          <Button
            variant="secondary"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw />
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      <div className="space-y-4">
        <TabStrip<ApprovalsTab>
          tabs={
            [
              { id: 'queue', label: 'To approve', badge: query.data?.length },
              { id: 'mine', label: 'Submitted by me', badge: mine.data?.length },
              { id: 'lookup', label: 'Find a request' },
            ] satisfies TabDefinition<ApprovalsTab>[]
          }
          active={tab}
          onChange={setTab}
          panelId={PANEL_ID}
          label="Approval sections"
        />

        <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
          {tab === 'mine' ? (
            <MyRequestsPanel />
          ) : tab === 'queue' ? (
            <div className="space-y-3">
              <CheckerCapabilities />

              <InboxFilters
                searchText={searchText}
                onSearchTextChange={(value) => narrow(() => setSearchText(value))}
                module={module}
                onModuleChange={(value) => narrow(() => setModule(value))}
                moduleOptions={moduleOptions}
                branchId={branchId}
                onBranchChange={(value) => narrow(() => setBranchId(value))}
                branchOptions={branchOptions}
                deadline={deadline}
                onDeadlineChange={(value) => narrow(() => setDeadline(value))}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearFilters}
              />

              <DataTable
                columns={columns}
                data={query.isPending ? undefined : pageRows}
                rowCount={filtered.length}
                pagination={pagination}
                onPaginationChange={setPagination}
                sorting={UNSORTED}
                onSortingChange={ignoreSortingChange}
                isLoading={query.isPending}
                isFetching={query.isFetching}
                error={query.isError ? query.error : null}
                onRetry={() => void query.refetch()}
                getRowId={(row) => row.requestId}
                onRowActivate={setSelected}
                pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
                emptyTitle={hasActiveFilters ? 'No requests match' : 'Nothing is waiting on you'}
                emptyDescription={
                  hasActiveFilters
                    ? 'The queue has requests in it, but none match what you have narrowed it to.'
                    : 'Every request you are authorised to countersign has been decided, or none has been raised. A request you submitted yourself never appears here — that is the control working, not a filter. Find it under "Submitted by me" instead.'
                }
                emptyAction={
                  hasActiveFilters ? (
                    <Button variant="secondary" size="sm" onClick={clearFilters}>
                      Clear the filters
                    </Button>
                  ) : null
                }
              />

              {query.data && query.data.length > 0 ? (
                <p className="text-2xs text-muted">
                  Oldest first, as the server orders it. The queue re-reads itself every minute, so a
                  request a colleague has just decided stops looking actionable on its own — and the
                  deadlines above keep counting down. Last read{' '}
                  {query.dataUpdatedAt > 0 ? formatDateTime(new Date(query.dataUpdatedAt)) : 'just now'}.
                </p>
              ) : null}
            </div>
          ) : (
            <RequestLookup />
          )}
        </div>
      </div>

      {selected ? (
        <ApprovalDetail
          key={selected.requestId}
          approval={selected}
          onOpenChange={(open) => {
            if (!open) {
              setSelected(null)
            }
          }}
        />
      ) : null}
    </>
  )
}
